import { type StageKind, typePhone } from "@nmt/shared";
import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useEffect,
  useId,
  useRef,
} from "react";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type Variant = "primary" | "secondary" | "quiet" | "danger";

export function Button({
  variant = "secondary",
  busy,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean }) {
  const styles: Record<Variant, string> = {
    primary: "bg-brand text-white hover:bg-brand-deep disabled:bg-brand/50",
    secondary: "bg-white text-ink border border-line hover:border-ink-soft disabled:text-muted",
    quiet: "text-ink-soft hover:bg-mist disabled:text-muted",
    danger: "bg-white text-danger border border-danger/30 hover:bg-danger-wash",
  };
  return (
    <button
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed",
        styles[variant],
        className,
      )}
      disabled={busy || rest.disabled}
      {...rest}
    >
      {busy && <span className="size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden />}
      {children}
    </button>
  );
}

const controlClass =
  "w-full rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-[15px] text-ink placeholder:text-muted/70 hover:border-ink-soft/50 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:bg-mist";

export function Field({
  label,
  hint,
  error,
  className,
  onChange,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string }) {
  const id = useId();
  // Phone numbers format themselves as they're typed: (405) 555-0123.
  const change: typeof onChange =
    rest.type === "tel" && onChange
      ? (e) => {
          e.target.value = typePhone(e.target.value);
          onChange(e);
        }
      : onChange;
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-semibold text-ink-soft">
        {label}
      </label>
      <input id={id} className={controlClass} aria-invalid={!!error} aria-describedby={hint ? `${id}-hint` : undefined} onChange={change} {...rest} />
      {hint && !error && (
        <p id={`${id}-hint`} className="mt-1 text-sm text-muted">
          {hint}
        </p>
      )}
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  );
}

export function TextAreaField({
  label,
  hint,
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: string; ref?: Ref<HTMLTextAreaElement> }) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-semibold text-ink-soft">
        {label}
      </label>
      <textarea id={id} className={cx(controlClass, "min-h-24")} {...rest} />
      {hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
    </div>
  );
}

export function SelectField({
  label,
  hint,
  children,
  className,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; hint?: string }) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-semibold text-ink-soft">
        {label}
      </label>
      <select id={id} className={controlClass} {...rest}>
        {children}
      </select>
      {hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
    </div>
  );
}

export function Badge({ tone, children }: { tone: "ok" | "wait" | "off" | "brand"; children: ReactNode }) {
  const tones = {
    ok: "bg-ok-wash text-ok",
    wait: "bg-wait-wash text-wait",
    off: "bg-mist text-ink-soft",
    brand: "bg-brand-wash text-brand-deep",
  };
  return <span className={cx("inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold", tones[tone])}>{children}</span>;
}

/** Stage tag: red while open, green when closed, gray when lost. */
export function StageBadge({ name, kind }: { name: string; kind: StageKind }) {
  return <Badge tone={kind === "closed" ? "ok" : kind === "lost" ? "off" : "brand"}>{name}</Badge>;
}

export function Panel({ children, className, id }: { children: ReactNode; className?: string; id?: string }) {
  return <section id={id} className={cx("scroll-mt-4 rounded-[var(--radius-panel)] bg-white shadow-[var(--shadow-card)]", className)}>{children}</section>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="px-5 py-10 text-center text-ink-soft">{children}</div>;
}

/** Underlined tabs for switching sections of a page. */
/**
 * Tabs: on a computer or tablet they wrap onto more lines when they don't fit (nothing scrolls
 * sideways); on a phone they're one dropdown showing the current tab.
 */
export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { value: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <>
      <div className="relative sm:hidden">
        <select aria-label="Section" value={value} onChange={(e) => onChange(e.target.value as T)} className={cx(controlClass, "appearance-none pr-10 font-semibold")}>
          {tabs.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <span aria-hidden className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-muted">
          ▾
        </span>
      </div>
      <div role="tablist" className="hidden flex-wrap gap-x-1 border-b border-line sm:flex">
        {tabs.map((t) => (
          <button
            key={t.value}
            role="tab"
            aria-selected={value === t.value}
            onClick={() => onChange(t.value)}
            className={cx(
              "-mb-px border-b-2 px-3 py-2.5 text-sm font-semibold whitespace-nowrap",
              value === t.value ? "border-brand text-ink" : "border-transparent text-muted hover:text-ink",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
    </>
  );
}

export function PanelHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-mist px-5 py-4">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {description && <p className="mt-0.5 max-w-prose text-sm text-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="page-title">{title}</h1>
        {description && <p className="mt-1.5 max-w-prose text-ink-soft">{description}</p>}
      </div>
      {action}
    </header>
  );
}

export function Alert({ tone = "danger", children }: { tone?: "danger" | "ok" | "info"; children: ReactNode }) {
  const tones = { danger: "bg-danger-wash text-danger", ok: "bg-ok-wash text-ok", info: "bg-mist text-ink-soft" };
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cx("rounded-[var(--radius-control)] px-3.5 py-2.5 text-sm", tones[tone])}>
      {children}
    </div>
  );
}

export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Wider for forms; "xl" for tables. */
  wide?: boolean | "xl";
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      className={cx(
        "m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] flex-col rounded-[var(--radius-panel)] border-0 bg-white p-0 text-ink shadow-2xl backdrop:bg-ink/50 open:flex",
        wide === "xl" ? "max-w-5xl" : wide ? "max-w-2xl" : "max-w-lg",
      )}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-mist px-5 py-4">
        <h2 className="text-lg font-semibold">{title}</h2>
        <button onClick={onClose} className="rounded-md p-1 text-muted hover:bg-mist hover:text-ink" aria-label="Close">
          <svg viewBox="0 0 20 20" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M5 5l10 10M15 5L5 15" />
          </svg>
        </button>
      </div>
      {/* flex-auto, not flex-1: Safari collapses a zero-basis child of a dialog that only has a max-height. */}
      <div className="min-h-0 flex-auto overflow-y-auto px-5 py-5">{children}</div>
      {footer && <div className="flex shrink-0 justify-end gap-2 border-t border-mist px-5 py-4">{footer}</div>}
    </dialog>
  );
}

export function Loading() {
  return (
    <div className="flex items-center gap-3 py-10 text-muted" role="status">
      <span className="size-4 animate-spin rounded-full border-2 border-brand border-r-transparent" aria-hidden />
      Loading…
    </div>
  );
}

export function Checkbox({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className={cx("flex gap-3 rounded-[var(--radius-control)] px-2 py-2", disabled ? "opacity-60" : "cursor-pointer hover:bg-mist")}>
      <input
        id={id}
        type="checkbox"
        className="mt-1 size-4 accent-[var(--color-brand)]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="block font-medium">{label}</span>
        {description && <span className="block text-sm text-muted">{description}</span>}
      </span>
    </label>
  );
}

export function formatDateTime(iso: string | null, tz?: string) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: tz }).format(new Date(iso));
}

export function formatDate(iso: string | null, tz?: string) {
  if (!iso) return "—";
  // Plain dates (YYYY-MM-DD) are calendar days, not moments: don't shift them by time zone.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeZone: "UTC" }).format(new Date(`${iso}T00:00:00Z`));
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeZone: tz }).format(new Date(iso));
}

/** "3 days ago", "today". */
export function ago(iso: string) {
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 60) return `${days} days ago`;
  return `${Math.floor(days / 30)} months ago`;
}

export function formatCents(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

/** "1,500" or "$1,500.00" → 150000 cents; empty → null; anything else → NaN. */
export function parseDollars(input: string): number | null {
  const clean = input.replace(/[$,\s]/g, "");
  if (!clean) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(clean)) return Number.NaN;
  return Math.round(Number(clean) * 100);
}

export { addressLine } from "@nmt/shared";

export { cx };
